import React, {useEffect, useLayoutEffect, useRef, useState} from "react";

// Swaps with style: every element of what's leaving winds up and whooshes off the left edge while the new
// pieces slide in from the right and settle, each on its own clock. Zed never flies; he stays and glides.
export const IN_AFTER_MS = 520, LEAVE_MS = 1150; // new pieces start after this; the leaving screen is gone after this

// When the leaving screen winds up a sling (data-sling), the new one waits for the launch before it comes in.
const SLING_WAIT_MS = 560;
export const inAfter = () => IN_AFTER_MS + (document.querySelector('.zoom-out[data-sling]') ? SLING_WAIT_MS : 0);

// What flies on its own: anything you can see as an object (an image, canvas, input, button, text, or a box with
// a background, border or shadow) flies as one piece with its contents; invisible layout containers are looked
// through, so their contents fly independently. Zed's slot never flies, and neither does anything holding it
// (only the things beside him do).
const VISUAL_TAGS = new Set(['IMG', 'CANVAS', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'svg', 'SVG', 'VIDEO']);
const isVisual = (el) => {
    if (VISUAL_TAGS.has(el.tagName) || el.dataset.fly === 'fade' || el.dataset.fly === 'zoom') return true;
    if ([...el.childNodes].some(n => n.nodeType === 3 && n.textContent.trim())) return true;
    const cs = getComputedStyle(el);
    return cs.backgroundImage !== 'none' || (cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent') ||
        parseFloat(cs.borderTopWidth) > 0 || parseFloat(cs.borderLeftWidth) > 0 || cs.boxShadow !== 'none';
};
const isInline = (el) => getComputedStyle(el).display === 'inline'; // transforms don't apply to inline boxes
const pieces = (el) => [...el.children].flatMap(child => {
    if (child.hasAttribute('data-zed-slot')) return [];
    if (isInline(child)) return []; // its block parent moves it
    if (child.querySelector('[data-zed-slot]')) return pieces(child);
    if (!isVisual(child)) {
        const inner = pieces(child);
        // a wrapper holding only inline text (or nothing that can move on its own) flies as one piece
        return inner.length ? inner : [child];
    }
    return [child];
});

// Flies the pieces inside `root` out to the left or in from the right. Each element flies once per direction.
export const flyPieces = (root, cls) => {
    if (!root) return;
    const inBase = cls === 'fly-in' ? inAfter() : 0;
    pieces(root).filter(el => el.dataset.flown !== cls)
        .forEach((el, k) => {
        el.dataset.flown = cls;
        // each piece on its own clock: its own delay, speed and a slight tilt (out to the left, in from the right)
        // the new pieces wait until the old ones have mostly cleared, so nothing lands on top of what's leaving
        const out = cls === 'fly-out';
        el.style.setProperty('--delay', `${Math.round((out ? 0 : inBase) + Math.random() * 250)}ms`);
        el.style.setProperty('--dur', `${Math.round((out ? 550 : 600) + Math.random() * 300)}ms`);
        el.style.setProperty('--rot', `${((Math.random() - 0.5) * 8).toFixed(1)}deg`);
        el.style.setProperty('--dy', `${Math.round((Math.random() - 0.5) * 24)}px`);
        el.classList.remove('fly-in', 'fly-out', 'fade-in', 'fade-out', 'zoom-in', 'zoom-out');
        // data-fly="zoom" comes at you from the camera instead, in the order of data-fly-order
        if (el.dataset.fly === 'zoom') el.style.setProperty('--delay', `${(out ? 0 : inBase) + (+el.dataset.flyOrder || 0) * 220}ms`);
        void el.offsetWidth; // restart the animation
        // backgrounds (data-fly="fade") fade instead of flying
        el.classList.add(el.dataset.fly === 'fade' || el.dataset.fly === 'zoom' ? cls.replace('fly', el.dataset.fly) : cls);
    });
};


// A whole screen (the tour's steps): its root is the screen's page element.
export const flyScreen = (container, cls) => {
    let root = container && container.firstElementChild;
    // a wrapper that only carries settings (a game's theme) is looked through to the screen inside it
    while (root && root.hasAttribute('data-fly-through')) root = root.firstElementChild;
    if (!root) return;
    // the leaving screen sits on top while its pieces fly off: its own background would hide the new screen
    if (cls === 'fly-out') root.style.background = 'transparent';
    flyPieces(root, cls);
};

const FlyBlock = ({mode, children}) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (mode !== 'still') flyPieces(ref.current, mode === 'out' ? 'fly-out' : 'fly-in');
    }, [mode]);
    return <div ref={ref} style={mode === 'out' ? {position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none'} : undefined}>
        {children}
    </div>;
};

let swapId = 0;

// <FlySwap swapKey={question}>…</FlySwap>: when swapKey changes, what was there flies off and the new content
// flies in, in place. The first content just appears (the screen's own entrance covers it).
export const FlySwap = ({swapKey, children}) => {
    const prev = useRef({key: swapKey, node: children});
    const first = useRef(swapKey);
    const [leaving, setLeaving] = useState([]);
    useEffect(() => {
        if (prev.current.key !== swapKey) {
            const gone = {id: swapId++, node: prev.current.node};
            setLeaving(l => [...l, gone]);
            setTimeout(() => setLeaving(l => l.filter(x => x !== gone)), LEAVE_MS);
        }
        prev.current = {key: swapKey, node: children};
    });
    return <div style={{position: 'relative'}}>
        {leaving.map(l => <FlyBlock key={`out-${l.id}`} mode="out">{l.node}</FlyBlock>)}
        <FlyBlock key={`in-${swapKey}`} mode={swapKey === first.current ? 'still' : 'in'}>{children}</FlyBlock>
    </div>;
};
